import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';
import { Colors } from '../../themes/colors';
import { useThemeMode } from '../../themes/useThemeMode';
import { Button, ButtonProps } from '../crop-circle/Button';
import { ResetIcon } from '../crop-circle/icons';
import { tokens } from '../crop-circle/tokens';

type RestartButtonProps = ButtonProps & {
  disabled?: boolean;
};

/**
 * Mirrors `Button`'s own `getTextColor` state-for-state. It used to hand-roll a
 * different map that was wrong in dark mode on three of five branches:
 * `active` fills with `surfaceInverse` (WHITE in dark), so the constant-white
 * `textOnBrand` glyph vanished into it; `default` fills with
 * `surfaceInverseSoft` (#292929), so a hardcoded #2F2F2F glyph vanished too;
 * and the disabled glyph stayed a bright #D8D8D8 on an inert dark fill.
 * Each replacement is the token whose LIGHT value the literal already was (or
 * is within one shade of), so light mode is effectively unchanged.
 *
 * The one real light-mode shift is `disabled`: #D8D8D8 -> #B4B4B4. That is
 * deliberate, and NOT to be "corrected" to `fillDisabled` (#CCCCCC) just
 * because it is the closer light match. This icon sits on `Button`'s own
 * disabled fill, so it has to track `Button`'s disabled LABEL, which made the
 * same #CCCCCC -> #B4B4B4 move for the reason recorded in `getTextColor`:
 * `fillDisabled` is tuned as a fill against #262626 inputs and lands at
 * #4A4A4A on the #232323 disabled button in dark, ~1.5:1 and effectively
 * invisible. Matching the sibling label is worth the one-shade light shift.
 */
const getIconColor = (
  state: ButtonProps['state'],
  disabled: boolean
): string => {
  if (disabled) {
    return Colors.role.textDisabled;
  }

  switch (state) {
    case 'active':
      return Colors.role.textOnInverse;
    case 'warning':
      return Colors.role.textOnBrand;
    case 'outline':
      return tokens.colors.orange[600];
    case 'default':
    default:
      return Colors.role.textPrimary;
  }
};

export const RestartButton: FC<RestartButtonProps> = ({
  style,
  state: stateProp,
  disabled: disabledProp,
  accessibilityLabel,
  children,
  ...rest
}: RestartButtonProps) => {
  const { t } = useTranslation();
  // The icon colour below is resolved from role tokens at render time, and this
  // button is usually rendered inside a memoized floating bar, so it needs its
  // own subscription to repaint on a live mode flip.
  useThemeMode();
  const state = stateProp ?? 'active';
  const disabled = disabledProp ?? false;
  const iconColor = getIconColor(state, disabled);

  return (
    <Button
      {...rest}
      style={StyleSheet.flatten([styles.container, style])}
      state={state}
      disabled={disabled}
      leftIcon={<ResetIcon color={iconColor} size={24} />}
      accessibilityLabel={accessibilityLabel ?? t('common.reset')}
    >
      {children}
    </Button>
  );
};

const styles = StyleSheet.create({
  container: {
    width: 56,
    height: 56,
    minWidth: 56,
    maxHeight: 56,
    borderRadius: 99,
    marginRight: 16,
  },
});
